import React from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Flex, Image, Link, Text
} from 'components/ui';
import HistorySectionCard from 'components/Content/BeautyInsider/BeautyInsiderHistory/HistorySectionCard';
import HistorySectionStatus from 'components/Content/BeautyInsider/BeautyInsiderHistory/HistorySectionStatus';
import localeUtils from 'utils/LanguageLocale';
import {
    colors, mediaQueries, radii, space
} from 'style/config';
import { HISTORY_CARD_STYLES, HISTORY_SECTION_STATUS } from 'components/Content/BeautyInsider/BeautyInsiderHistory/historyConstants';
import EN from './locales/HistoryRewardsRedeemed_en_US';
import FR from './locales/HistoryRewardsRedeemed_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(EN, FR);
const PLACEHOLDER_SECTION_MIN_HEIGHT = space[9] - space[1];
const EMPTY_SECTION_MIN_HEIGHT = space[9] + space[7] + space[3];

/**
 * Renders the Rewards Redeemed History section from section presentation data.
 *
 * Supports loading, error, placeholder, empty, and populated statuses. Populated sections render reward thumbnails and
 * the View details purchase-history route.
 *
 * @param {Object} props
 * @param {Object} props.section Rewards Redeemed presentation data with thumbnails, viewDetail, and retry metadata.
 * @returns {React.ReactElement} Rewards Redeemed card.
 */
const HistoryRewardsRedeemed = ({ section }) => {
    const isEmpty = section.status === HISTORY_SECTION_STATUS.EMPTY;
    const isPlaceholder = section.status === HISTORY_SECTION_STATUS.PLACEHOLDER;
    const sectionMinHeight = isPlaceholder ? PLACEHOLDER_SECTION_MIN_HEIGHT : isEmpty ? EMPTY_SECTION_MIN_HEIGHT : undefined;

    const renderHeaderAction = () => {
        if (!section.viewDetail) {
            return null;
        }

        return (
            <Link
                color='blue'
                href={section.viewDetail.href}
                children={section.viewDetail.label}
            />
        );
    };

    const renderContent = () => {
        if (
            section.status === HISTORY_SECTION_STATUS.LOADING ||
            section.status === HISTORY_SECTION_STATUS.ERROR ||
            section.status === HISTORY_SECTION_STATUS.PLACEHOLDER
        ) {
            return (
                <HistorySectionStatus
                    loadingLabel={getText('loading')}
                    numberOfLoadingCards={2}
                    retryLabel={getText('retry')}
                    section={section}
                />
            );
        }

        if (isEmpty) {
            return (
                <Box css={styles.emptyContent}>
                    {section.emptyState?.illustration && (
                        <Image
                            display='block'
                            size={48}
                            src={section.emptyState.illustration}
                        />
                    )}
                    <Text
                        is='p'
                        lineHeight='tight'
                        children={section.emptyState?.body}
                    />
                </Box>
            );
        }

        return (
            <Flex
                gap={2}
                flexWrap='wrap'
            >
                {section.thumbnails.map((thumbnail, index) => (
                    <Image
                        key={thumbnail.key || `${thumbnail.skuId || thumbnail.productId || thumbnail.imageUrl}-${index}`}
                        src={thumbnail.imageUrl}
                        alt={thumbnail.altText}
                        width={56}
                        height={56}
                        css={[styles.thumbnail, index === 5 && styles.desktopOnlyThumbnail]}
                    />
                ))}
            </Flex>
        );
    };

    return (
        <HistorySectionCard
            section={section}
            dataTestId='history-rewards-redeemed-section'
            headerAction={renderHeaderAction()}
            headerMarginBottom={4}
            minHeight={sectionMinHeight}
        >
            {renderContent()}
        </HistorySectionCard>
    );
};

HistoryRewardsRedeemed.propTypes = {
    section: PropTypes.shape({
        title: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        cardStyle: PropTypes.oneOf(Object.values(HISTORY_CARD_STYLES)),
        thumbnails: PropTypes.arrayOf(PropTypes.object),
        viewDetail: PropTypes.object,
        placeholder: PropTypes.object,
        emptyState: PropTypes.shape({
            illustration: PropTypes.string,
            body: PropTypes.string
        }),
        error: PropTypes.object,
        retry: PropTypes.func
    }).isRequired
};

const styles = {
    emptyContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: space[2],
        alignItems: 'flex-start'
    },
    thumbnail: {
        border: `1px solid ${colors.lightGray}`,
        borderRadius: radii[2],
        objectFit: 'contain'
    },
    desktopOnlyThumbnail: {
        [mediaQueries.smMax]: {
            display: 'none'
        }
    }
};

export default wrapFunctionalComponent(HistoryRewardsRedeemed, 'HistoryRewardsRedeemed');
